/**
 * Pusher Realtime Provider
 *
 * Pusher SaaS implementation of the RealtimeProvider interface.
 * Provides fallback support for environments where Centrifugo is unavailable.
 */

import Pusher, { type Channel } from 'pusher-js'
import { environment } from '@/shared/env'
import { RealtimeProvider, RealtimeInitParams } from './types'
import {
  SHOP_CHANNEL_EVENTS,
  NOTIFICATION_CHANNEL_EVENTS,
  dispatchDomEvent,
  resolveForceLogoutChannelName,
  shouldHandleForceLogout,
} from './utils'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface PusherProviderState {
  pusher: Pusher | null
  shopChannel: Channel | null
  notificationChannel: Channel | null
  forceLogoutChannel: Channel | null
}

// ---------------------------------------------------------------------------
// Pusher Provider Implementation
// ---------------------------------------------------------------------------

export class PusherProvider implements RealtimeProvider {
  private state: PusherProviderState = {
    pusher: null,
    shopChannel: null,
    notificationChannel: null,
    forceLogoutChannel: null,
  }

  /**
   * Bind a Pusher event to a channel and dispatch the corresponding DOM event.
   */
  private bindAndDispatch(channel: Channel, pusherEvent: string, domEvent: string): void {
    channel.bind(pusherEvent, (data: unknown) => {
      console.debug(`[Pusher] Received '${pusherEvent}' on channel '${channel.name}', dispatching '${domEvent}'`, data)
      dispatchDomEvent(domEvent, data)
    })
  }

  /**
   * Set up the shop channel with all event bindings.
   */
  private setupShopChannel(pusher: Pusher, referallshopName: string, group_id?: string): Channel {
    const channel = pusher.subscribe(referallshopName)
    console.info(`[Pusher] Subscribed to shop channel: ${referallshopName}`)
    
    for (const [pusherEvent, domEvent] of Object.entries(SHOP_CHANNEL_EVENTS)) {
      this.bindAndDispatch(channel, pusherEvent, domEvent)
    }
    if (group_id) {
      // Bind a group-scoped variant of 'my-event' so only that group's members receive it
      console.info(`[Pusher] Binding group-scoped event for group: ${group_id}`)
      this.bindAndDispatch(channel, `my-event-${group_id}`, 'new-message')
    }
    return channel
  }

  /**
   * Set up the notification channel with all event bindings.
   */
  private setupNotificationChannel(pusher: Pusher, authkey: string): Channel {
    const channel = pusher.subscribe(authkey)
    console.info(`[Pusher] Subscribed to notification channel: ${authkey}`)
    
    for (const [pusherEvent, domEvent] of Object.entries(NOTIFICATION_CHANNEL_EVENTS)) {
      this.bindAndDispatch(channel, pusherEvent, domEvent)
    }
    return channel
  }

  /**
   * Set up the force-logout channel (role_id, base64-encoded).
   * Dispatches a 'force-logout' DOM event when the server sends 'force_logout'.
   */
  private setupForceLogoutChannel(pusher: Pusher, token: string, refreshToken?: string): Channel | null {
    const channelName = resolveForceLogoutChannelName(token)
    if (!channelName) {
      console.warn('[Pusher] Could not resolve force-logout channel name')
      return null
    }

    const channel = pusher.subscribe(channelName)
    console.info(`[Pusher] Subscribed to force-logout channel: ${channelName}`)
    
    channel.bind('force_logout', (data: any) => {
      console.debug('[Pusher] Received force_logout event', data)
      if (!shouldHandleForceLogout(data, refreshToken)) {
        console.debug('[Pusher] Skipping force_logout: validation failed')
        return
      }
      console.info('[Pusher] Dispatching force-logout event')
      dispatchDomEvent('force-logout', data)
    })
    return channel
  }

  initRealtime({ referallshopName, group_id, authkey, token, refreshToken }: RealtimeInitParams): void {
    // Idempotency guard — prevent re-init on navigation
    if (this.state.pusher) {
      console.debug('[Pusher] Already initialized, skipping re-init')
      return
    }

    if (!referallshopName) {
      console.warn('[PusherProvider] Cannot initialize: referallshopName is required')
      return
    }

    const key = environment('VUE_APP_PUSHER_KEY')
    const cluster = environment('VUE_APP_PUSHER_CLUSTER')

    if (!key || !cluster) {
      console.warn('[PusherProvider] VUE_APP_PUSHER_KEY or VUE_APP_PUSHER_CLUSTER not set')
      return
    }

    const pusher = new Pusher(key, {
      cluster,
      forceTLS: true,
    })

    // Setup global connection event listeners
    pusher.connection.bind('connecting', () => {
      console.info('[Pusher] Connecting...')
    })
    pusher.connection.bind('connected', () => {
      console.info('[Pusher] Connected')
    })
    pusher.connection.bind('unavailable', () => {
      console.warn('[Pusher] Connection unavailable')
    })
    pusher.connection.bind('failed', () => {
      console.error('[Pusher] Connection failed')
    })
    pusher.connection.bind('disconnected', () => {
      console.info('[Pusher] Disconnected')
    })

    console.info('[Pusher] Initialized with key:', key, 'cluster:', cluster)

    this.state.shopChannel = this.setupShopChannel(pusher, referallshopName, group_id)
    this.state.notificationChannel = authkey ? this.setupNotificationChannel(pusher, authkey) : null
    this.state.forceLogoutChannel = token ? this.setupForceLogoutChannel(pusher, token, refreshToken) : null
    this.state.pusher = pusher

    console.info('[Pusher] Initialization complete')
  }

  disconnectRealtime(): void {
    if (!this.state.pusher) {
      console.debug('[Pusher] Already disconnected, skipping')
      return
    }

    console.info('[Pusher] Disconnecting...')

    if (this.state.shopChannel) {
      console.debug(`[Pusher] Unsubscribing from shop channel: ${this.state.shopChannel.name}`)
      this.state.pusher.unsubscribe(this.state.shopChannel.name)
    }
    if (this.state.notificationChannel) {
      console.debug(`[Pusher] Unsubscribing from notification channel: ${this.state.notificationChannel.name}`)
      this.state.pusher.unsubscribe(this.state.notificationChannel.name)
    }
    if (this.state.forceLogoutChannel) {
      console.debug(`[Pusher] Unsubscribing from force-logout channel: ${this.state.forceLogoutChannel.name}`)
      this.state.forceLogoutChannel.unbind_all()
      this.state.pusher.unsubscribe(this.state.forceLogoutChannel.name)
    }

    this.state.pusher.disconnect()
    this.state.pusher = null
    this.state.shopChannel = null
    this.state.notificationChannel = null
    this.state.forceLogoutChannel = null

    console.info('[Pusher] Disconnection complete')
  }

  isConnected(): boolean {
    return this.state.pusher !== null
  }
}
